import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { FileText, CheckCircle, Pen, User, Loader2, AlertCircle, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import MarkdownContent from '@/components/MarkdownContent';
import { useNavigate } from 'react-router-dom';

export default function PractitionerOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState('agreement'); // 'agreement' | 'profile' | 'done'
  const [sections, setSections] = useState([]);
  const [checkpoints, setCheckpoints] = useState({});
  const [signatureName, setSignatureName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingAgreements, setExistingAgreements] = useState([]);
  const [intro, setIntro] = useState('Please read each section carefully and acknowledge by checking the box.');
  const [signaturePrompt, setSignaturePrompt] = useState(
    'By typing your full legal name below, you acknowledge that you have read and agree to all sections above.'
  );

  // Profile form — mirrors the admin Create Practitioner form (Admin → Resources/Guides)
  const [profile, setProfile] = useState({
    display_name: '', title: '', bio: '', specialties: [],
    phone: '', contact_email: '', profile_photo_url: '',
    website_url: '', social_links: {},
    custom_link_1_label: '', custom_link_1_url: '',
    custom_link_2_label: '', custom_link_2_url: '',
    custom_link_3_label: '', custom_link_3_url: '',
  });
  const [specialtyInput, setSpecialtyInput] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const setSocial = (key, value) =>
    setProfile(p => ({ ...p, social_links: { ...p.social_links, [key]: value } }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/practitioner-forms/upload-photo`, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setProfile(p => ({ ...p, profile_photo_url: res.data.file_url }));
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const [accessDenied, setAccessDenied] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [templateRes, agreementsRes, statusRes] = await Promise.all([
        axios.get(`${API}/practitioner-forms/agreement-template`, { headers }),
        axios.get(`${API}/practitioner-forms/my-agreements`, { headers }),
        axios.get(`${API}/practitioner-forms/my-onboarding-status`, { headers }),
      ]);
      setSections(templateRes.data.sections);
      if (templateRes.data.intro) setIntro(templateRes.data.intro);
      if (templateRes.data.signature_prompt) setSignaturePrompt(templateRes.data.signature_prompt);
      setExistingAgreements(agreementsRes.data);

      // Work out where they actually are, so a reload never drops someone who has
      // finished back onto an empty form.
      if (statusRes.data?.profile_submitted) {
        setStep('done');
      } else if (statusRes.data?.agreement_signed || agreementsRes.data.length > 0) {
        setStep('profile');
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setAccessDenied(true);
        toast.info('Practitioner onboarding is reserved for Free-Comp members.');
        setTimeout(() => { navigate('/dashboard/portal'); }, 2500);
      } else {
        console.error('Failed to load form', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const allChecked = sections.every(s => s.requires_ack === false || checkpoints[s.key]);

  const handleSubmitAgreement = async () => {
    if (!allChecked || !signatureName.trim()) {
      toast.error('Please acknowledge all sections and provide your signature');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/practitioner-forms/submit-agreement`, {
        checkpoints,
        signature_name: signatureName.trim(),
      }, { headers });
      toast.success('Agreement signed successfully!');
      window.dispatchEvent(new Event('onboarding-status-changed'));
      setStep('profile');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit agreement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitProfile = async () => {
    if (!profile.display_name.trim() || !profile.title.trim() || !profile.bio.trim()) {
      toast.error('Please fill in display name, title, and bio');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/practitioner-forms/submit-profile`, profile, { headers });
      toast.success('Profile saved! Your practitioner placard has been updated.');
      window.dispatchEvent(new Event('onboarding-status-changed'));
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSubmitting(false);
    }
  };

  const addSpecialty = () => {
    if (specialtyInput.trim() && !profile.specialties.includes(specialtyInput.trim())) {
      setProfile(p => ({ ...p, specialties: [...p.specialties, specialtyInput.trim()] }));
      setSpecialtyInput('');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal-400" /></div>;
  }

  if (accessDenied) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="onboarding-access-denied">
        <Card className="bg-white/5 border-amber-500/30 text-center py-12">
          <CardContent>
            <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-amber-300 mb-2">Onboarding Not Available</h2>
            <p className="text-gray-400 mb-6">
              The practitioner onboarding wizard is reserved for Free-Comp members
              (Practitioners, Experts, and Contributors). Marketplace vendors agree
              to platform policies during their vendor application.
            </p>
            <Button onClick={() => navigate('/dashboard/portal')} className="bg-teal-600 hover:bg-teal-700">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tier headline (only practitioner_agreement is valid now; vendor_agreement deprecated)
  const headerTitle = 'Practitioner Agreement';

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-onboarding-form data-testid="practitioner-onboarding">
      {/*
        Every field on this page sits on a dark surface, but the shared Input/Textarea
        default to near-black text (a light-theme token), which made anything typed here
        effectively invisible. Set once here so it covers every field on the page.
      */}
      <style>{`
        [data-onboarding-form] input,
        [data-onboarding-form] textarea {
          color: #f3f4f6;
          background-color: rgba(255,255,255,0.08);
          border-color: rgba(255,255,255,0.22);
        }
        [data-onboarding-form] input::placeholder,
        [data-onboarding-form] textarea::placeholder { color: #a2acbd; opacity: 1; }
        [data-onboarding-form] input[type="file"]::file-selector-button {
          color: #f3f4f6; background-color: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.22); border-radius: 6px;
          padding: 2px 10px; margin-right: 10px; cursor: pointer;
        }
      `}</style>
      {/* What this involves — they land here cold, so say it plainly */}
      {step !== 'done' && (
        <Card className="bg-white/5 border-white/10" data-testid="onboarding-what-to-expect">
          <CardContent className="pt-5">
            <h2 className="text-lg font-semibold text-teal-300">Welcome — here&apos;s what this involves</h2>
            <p className="text-sm text-gray-300 mt-1">
              Three steps, about ten minutes. You can stop and come back to it — nothing is lost.
            </p>
            <ol className="mt-4 space-y-3">
              {[
                ['Read and sign the agreement', 'How we work together, what we ask of you, and what you can expect from us.'],
                ['Tell us about your practice', 'Your name as you want it shown, what you do, and a short bio. This builds your listing for you.'],
                ['We review and publish', "We check your listing and put it live. You'll hear from us when it is."],
              ].map(([title, detail], i) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/20 text-xs font-semibold text-teal-300">
                    {i + 1}
                  </span>
                  <span className="text-sm">
                    <span className="font-medium text-gray-100">{title}</span>
                    <span className="block text-gray-400">{detail}</span>
                  </span>
                </li>
              ))}
            </ol>
            <p className="text-xs text-gray-500 mt-4">
              Nothing here is published until we&apos;ve reviewed it with you.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Progress Indicator */}
      <div className="flex items-center gap-4 mb-8">
        <div className={`flex items-center gap-2 ${step === 'agreement' ? 'text-teal-400' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'agreement' ? 'bg-teal-500 text-white' : existingAgreements.length > 0 ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-400'}`}>
            {existingAgreements.length > 0 ? <CheckCircle className="w-5 h-5" /> : '1'}
          </div>
          <span className="text-sm font-medium">Agreement</span>
        </div>
        <div className="flex-1 h-px bg-gray-700" />
        <div className={`flex items-center gap-2 ${step === 'profile' ? 'text-teal-400' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'profile' ? 'bg-teal-500 text-white' : step === 'done' ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-400'}`}>
            {step === 'done' ? <CheckCircle className="w-5 h-5" /> : '2'}
          </div>
          <span className="text-sm font-medium">Profile</span>
        </div>
        <div className="flex-1 h-px bg-gray-700" />
        <div className={`flex items-center gap-2 ${step === 'done' ? 'text-teal-400' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'done' ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-400'}`}>
            {step === 'done' ? <CheckCircle className="w-5 h-5" /> : '3'}
          </div>
          <span className="text-sm font-medium">Complete</span>
        </div>
      </div>

      {/* Step 1: Agreement Form */}
      {step === 'agreement' && (
        <div className="space-y-4">
          <Card className="bg-white/5 border-teal-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" data-testid="agreement-title">
                <FileText className="w-5 h-5 text-teal-400" />
                {headerTitle}
              </CardTitle>
              <p className="text-sm text-gray-400 mt-1" data-testid="agreement-intro">
                {intro}
              </p>
            </CardHeader>
          </Card>

          {sections.map((section) => {
            const needsAck = section.requires_ack !== false;
            return (
              <Card key={section.key} className="bg-white/5 border-white/10">
                <CardContent className="pt-5">
                  <div className="flex items-start gap-3">
                    {needsAck ? (
                      <Checkbox
                        checked={checkpoints[section.key] || false}
                        onCheckedChange={(checked) => setCheckpoints(p => ({ ...p, [section.key]: !!checked }))}
                        className="mt-1 h-5 w-5 border-2 border-teal-300/80 bg-white/15 data-[state=checked]:bg-teal-400 data-[state=checked]:border-teal-300 data-[state=checked]:text-slate-900"
                        data-testid={`checkpoint-${section.key}`}
                      />
                    ) : (
                      <Info className="mt-1 h-5 w-5 shrink-0 text-teal-300/70" />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-2">{section.title}</h3>
                      <MarkdownContent content={section.content} tone="light-ink" className="text-sm" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Signature */}
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-5">
              <div className="flex items-start gap-3">
                <Pen className="w-5 h-5 text-teal-400 mt-1" />
                <div className="flex-1 space-y-3">
                  <h3 className="font-semibold text-white">Digital Signature</h3>
                  <p className="text-sm text-gray-400" data-testid="signature-prompt">
                    {signaturePrompt}
                  </p>
                  <Input
                    value={signatureName}
                    onChange={e => setSignatureName(e.target.value)}
                    placeholder="Type your full legal name"
                    className="bg-white/5 border-white/10 text-lg italic"
                    data-testid="signature-input"
                  />
                  <p className="text-xs text-gray-500">
                    Date: {new Date().toLocaleDateString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Button
            onClick={handleSubmitAgreement}
            disabled={!allChecked || !signatureName.trim() || submitting}
            className="w-full bg-teal-600 hover:bg-teal-700 py-6 text-base"
            data-testid="submit-agreement-btn"
          >
            {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle className="w-5 h-5 mr-2" />}
            Sign Agreement
          </Button>
        </div>
      )}

      {/* Step 2: Profile Questionnaire */}
      {step === 'profile' && (
        <div className="space-y-4">
          <Card className="bg-white/5 border-teal-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-teal-400" />
                Practitioner Profile
              </CardTitle>
              <p className="text-sm text-gray-400 mt-1">
                Fill out your profile to auto-populate your Resources/Guides practitioner placard.
              </p>
            </CardHeader>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-5 space-y-4">
              <div>
                <label className="text-sm text-gray-300 mb-1 block">Display Name *</label>
                <Input
                  value={profile.display_name}
                  onChange={e => setProfile(p => ({...p, display_name: e.target.value}))}
                  placeholder="How you want to appear on your placard"
                  data-testid="profile-display-name"
                />
              </div>

              <div>
                <label className="text-sm text-gray-300 mb-1 block">Professional Title *</label>
                <Input
                  value={profile.title}
                  onChange={e => setProfile(p => ({...p, title: e.target.value}))}
                  placeholder="e.g., Energy Healer, Spiritual Coach, Meditation Guide"
                  data-testid="profile-title"
                />
              </div>

              <div>
                <label className="text-sm text-gray-300 mb-1 block">Bio *</label>
                <Textarea
                  value={profile.bio}
                  onChange={e => setProfile(p => ({...p, bio: e.target.value}))}
                  placeholder="Tell the community about your practice, experience, and approach..."
                  rows={5}
                  data-testid="profile-bio"
                />
              </div>

              <div>
                <label className="text-sm text-gray-300 mb-1 block">Specialties</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={specialtyInput}
                    onChange={e => setSpecialtyInput(e.target.value)}
                    placeholder="Add a specialty"
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
                  />
                  <Button variant="outline" onClick={addSpecialty} size="sm"
                    className="border-white/25 bg-white/10 text-gray-100 hover:bg-white/20 hover:text-white">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.specialties.map((s, i) => (
                    <Badge key={i} variant="outline" className="bg-teal-500/20 text-teal-300 border-teal-500/30 cursor-pointer"
                      onClick={() => setProfile(p => ({...p, specialties: p.specialties.filter((_, j) => j !== i)}))}
                    >
                      {s} x
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">Phone Number</label>
                  <Input
                    type="tel"
                    value={profile.phone}
                    onChange={e => setProfile(p => ({...p, phone: e.target.value}))}
                    placeholder="(555) 123-4567"
                    data-testid="profile-phone"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">Contact Email</label>
                  <Input
                    type="email"
                    value={profile.contact_email}
                    onChange={e => setProfile(p => ({...p, contact_email: e.target.value}))}
                    placeholder="Leave blank to use your account email"
                    data-testid="profile-contact-email"
                  />
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <p className="text-sm font-semibold text-gray-100 mb-3">Photo</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-300 mb-1 block">
                      Upload a photo (JPG, PNG, GIF, WebP)
                    </label>
                    <Input
                      type="file"
                      accept=".jpg,.jpeg,.png,.gif,.webp"
                      onChange={e => uploadPhoto(e.target.files?.[0])}
                      className="cursor-pointer"
                      data-testid="profile-photo-upload"
                    />
                    {uploadingPhoto && (
                      <p className="text-xs text-teal-300 mt-1 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Uploading…
                      </p>
                    )}
                    {profile.profile_photo_url && !uploadingPhoto && (
                      <p className="text-xs text-green-400 mt-1" data-testid="profile-photo-ok">
                        Photo added
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-300 mb-1 block">Or paste an image URL</label>
                    <Input
                      value={profile.profile_photo_url}
                      onChange={e => setProfile(p => ({...p, profile_photo_url: e.target.value}))}
                      placeholder="https://..."
                      data-testid="profile-photo-url"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <p className="text-sm font-semibold text-gray-100 mb-3">Links</p>
                <label className="text-sm text-gray-300 mb-1 block">Website</label>
                <Input
                  value={profile.website_url}
                  onChange={e => setProfile(p => ({...p, website_url: e.target.value}))}
                  placeholder="https://yourwebsite.com"
                  data-testid="profile-website"
                />
              </div>

              <div className="border-t border-white/10 pt-4">
                <p className="text-sm font-semibold text-gray-100 mb-3">Social Media</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    ['facebook', 'Facebook', 'https://facebook.com/...'],
                    ['instagram', 'Instagram', 'https://instagram.com/...'],
                    ['linkedin', 'LinkedIn', 'https://linkedin.com/in/...'],
                    ['twitter', 'Twitter/X', 'https://twitter.com/...'],
                    ['youtube', 'YouTube', 'https://youtube.com/...'],
                    ['tiktok', 'TikTok', 'https://tiktok.com/@...'],
                    ['spotify', 'Spotify', 'https://open.spotify.com/...'],
                  ].map(([key, label, placeholder]) => (
                    <div key={key}>
                      <label className="text-sm text-gray-300 mb-1 block">{label}</label>
                      <Input
                        value={profile.social_links[key] || ''}
                        onChange={e => setSocial(key, e.target.value)}
                        placeholder={placeholder}
                        data-testid={`profile-social-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <p className="text-sm font-semibold text-gray-100 mb-1">Custom Links (optional)</p>
                <p className="text-xs text-gray-400 mb-3">
                  Anything else you want on your listing — a podcast, a booking page, a free guide.
                </p>
                <div className="space-y-3">
                  {[1, 2, 3].map(n => (
                    <div key={n} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        value={profile[`custom_link_${n}_label`]}
                        onChange={e => setProfile(p => ({...p, [`custom_link_${n}_label`]: e.target.value}))}
                        placeholder={`Link ${n} label`}
                        data-testid={`profile-custom-link-${n}-label`}
                      />
                      <Input
                        value={profile[`custom_link_${n}_url`]}
                        onChange={e => setProfile(p => ({...p, [`custom_link_${n}_url`]: e.target.value}))}
                        placeholder="https://..."
                        data-testid={`profile-custom-link-${n}-url`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                toast.info("Saved your agreement — come back any time to finish your profile.");
                navigate('/dashboard/portal');
              }}
              className="flex-1 border-white/25 bg-white/10 text-gray-100 hover:bg-white/20 hover:text-white"
              data-testid="finish-later-btn"
            >
              Finish later
            </Button>
            <Button
              onClick={handleSubmitProfile}
              disabled={!profile.display_name.trim() || !profile.title.trim() || !profile.bio.trim() || submitting}
              className="flex-1 bg-teal-600 hover:bg-teal-700"
              data-testid="submit-profile-btn"
            >
              {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle className="w-5 h-5 mr-2" />}
              Save Profile
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Complete */}
      {step === 'done' && (
        <Card className="bg-white/5 border-teal-500/30" data-testid="onboarding-complete-card">
          <CardContent className="pt-10 pb-10">
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-teal-300 mb-2">That&apos;s everything — thank you</h2>
              <p className="text-gray-300 max-w-xl mx-auto">
                Your agreement is signed and on file, and your practice details are in.
                We&apos;ve emailed you a copy for your records.
              </p>
            </div>

            <div className="mt-8 max-w-xl mx-auto space-y-4 text-left">
              <p className="text-sm font-semibold text-gray-100">What happens next</p>
              {[
                ['We review your listing', 'Usually within a few days. We may come back to you with a question or two.'],
                ['We publish it', "Once it's live, members can find you in Resources & Guides. You'll get an email when that happens."],
                ['Your Hub is already open', 'You can set up bookings, sessions and prices now so everything is ready for launch.'],
              ].map(([title, detail]) => (
                <div key={title} className="flex gap-3">
                  <CheckCircle className="w-4 h-4 text-teal-400 mt-1 shrink-0" />
                  <p className="text-sm">
                    <span className="font-medium text-gray-100">{title}</span>
                    <span className="block text-gray-400">{detail}</span>
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button
                onClick={() => navigate('/dashboard/practitioner-hub')}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="onboarding-go-to-hub"
              >
                Open my Practitioner Hub
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard/portal')}
                className="border-white/25 bg-white/10 text-gray-100 hover:bg-white/20 hover:text-white"
                data-testid="onboarding-back-to-dashboard"
              >
                Back to dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
